import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Quote, Target, MessageCircle, CheckCircle2, RotateCcw, Users, ChevronDown, ChevronUp } from 'lucide-react';
import {
  getLivePlaybook,
  processLivePlaybook,
  retryLivePlaybook,
  updatePlaybookSpeakerSide,
  updatePlaybookTask,
} from '../../lib/mavisApi';
import { meetingErrorMessage } from './MavisMeetingPanel';
import {
  visibleQuestions,
  bantKeys,
  meddpiccKeys,
  playbookFields,
  evidenceByField,
  fieldState,
  meetingSourceUrl,
} from './livePlaybookModel';

type Framework = 'bant' | 'meddpicc';
type InsightTab = 'hooks' | 'playbook' | 'questions';
type TaskSaveHandler = (task: any, call: any, status: string, notes: string) => void;

interface Props {
  meetingId?: string;
  opportunityId: string;
  live?: boolean;
  view?: 'meeting' | 'sync' | 'tasks' | 'stakeholders';
  qualification?: any;
  onChange?: () => void;
}

const LIVE_POLL_INTERVAL_MS = 2_000;
const IDLE_POLL_INTERVAL_MS = 5_000;

// Labels for evidence fields that are not BANT/MEDDPICC keys (Now-to-Value fields).
const EXTRA_FIELD_LABELS: Record<string, string> = {
  current_state: 'Current State',
  negative_consequences: 'Negative Consequences',
  future_state: 'Future State',
  positive_business_outcomes: 'Positive Business Outcomes',
  required_capabilities: 'Required Capabilities',
  timelines_and_milestones: 'Timelines & Milestones',
  personas_required: 'Personas Required',
};

const QUESTION_STATE_LABELS = {
  suggested: 'Suggested',
  asked_unanswered: 'Asked, unanswered',
  asked_answered: 'Asked and answered',
};

const time = (n: any) => (typeof n === 'number' ? `${Math.floor(n / 60)}:${String(Math.floor(n % 60)).padStart(2, '0')}` : 'Source');

const fieldLabel = (field: string) => playbookFields[field as keyof typeof playbookFields] ?? EXTRA_FIELD_LABELS[field];

function sideLabel(side: string): string {
  if (side === 'customer') return 'Customer reported';
  if (side === 'internal') return 'Seller observation';
  if (side === 'partner') return 'Partner observation';
  return 'Speaker side unknown';
}

function fieldStateClass(state: string): string {
  if (state === 'Customer reported') return 'text-app-brand';
  if (state === 'Conflicting statements') return 'text-app-warning';
  return 'text-app-muted';
}

function hasUnknownSpeakers(call: any): boolean {
  return Object.values(call?.participants ?? {}).some((p: any) => p.side === 'unknown');
}

/** The playbook needs polling while a call is still being analysed or has unprocessed speech. */
function isAnalysing(data: any): boolean {
  return Boolean(data?.calls?.some((c: any) =>
    c.processing_status === 'processing' || (c.processing_status === 'idle' && c.pending_events > 0)));
}

function TaskItem({ task, call, onSave, busy }: { task: any; call: any; onSave: TaskSaveHandler; busy: boolean }) {
  const [notes, setNotes] = useState(task.notes ?? '');
  useEffect(() => setNotes(task.notes ?? ''), [task.notes]);

  return (
    <div className="rounded-xl border border-app-line p-3 space-y-2">
      <div className="flex items-start gap-2">
        <CheckCircle2 className={'w-4 h-4 mt-0.5 shrink-0 ' + (task.status === 'completed' ? 'text-app-brand' : 'text-app-muted')} />
        <p className="text-sm text-app-ink flex-1">{task.action}</p>
        <select
          aria-label={'Status for ' + task.action}
          className="bg-app-canvas border border-app-line rounded-lg text-xs p-1.5"
          value={task.status}
          disabled={busy}
          onChange={(e) => onSave(task, call, e.target.value, notes)}
        >
          <option value="open">Open</option>
          <option value="in_progress">In progress</option>
          <option value="completed">Completed</option>
        </select>
      </div>
      <p className="text-xs text-app-muted">{task.owner ?? 'Owner not stated'}{task.due ? ` · ${task.due}` : ''} · {call.meeting_title}</p>
      <details className="text-xs text-app-muted">
        <summary className="cursor-pointer">Evidence and notes</summary>
        <p className="mt-2 leading-5">{task.speaker}: “{task.quote}”</p>
        <textarea
          aria-label={'Notes for ' + task.action}
          maxLength={2000}
          className="input-field !text-xs mt-2"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => {
            if (notes !== task.notes) onSave(task, call, task.status, notes);
          }}
          placeholder="Add a note"
        />
      </details>
    </div>
  );
}

function TaskList({ calls, busy, onSave }: { calls: any[]; busy: boolean; onSave: TaskSaveHandler }) {
  const tasks = calls.flatMap((c: any) => c.tasks.map((task: any) => ({ task, call: c })));
  if (!tasks.length) {
    const fullyProcessed = calls.length
      && calls.every((c: any) => c.processing_status === 'idle' && c.pending_events === 0 && c.processed_count);
    return (
      <div className="space-y-3">
        <p className="text-sm text-app-muted">
          {fullyProcessed ? 'No follow up actions were agreed in the captured transcript.' : 'No agreed actions captured yet.'}
        </p>
      </div>
    );
  }

  const byOwner = tasks.reduce((groups: Record<string, any[]>, entry: any) => {
    const key = entry.task.owner ?? 'Owner not stated';
    (groups[key] ??= []).push(entry);
    return groups;
  }, {});

  return (
    <div className="space-y-3">
      {Object.entries(byOwner).map(([owner, items]) => (
        <div key={owner} className="space-y-2">
          <h4 className="text-xs font-semibold text-app-ink">{owner}</h4>
          {((items ?? []) as any[]).map(({ task, call }: any) => (
            <TaskItem key={task.id} task={task} call={call} busy={busy} onSave={onSave} />
          ))}
        </div>
      ))}
    </div>
  );
}

interface QualificationMatrixProps {
  calls: any[];
  data: any;
  qualification?: any;
  opportunityId: string;
  framework: Framework;
  onFrameworkChange: (framework: Framework) => void;
  openField: string | null;
  onOpenFieldChange: (field: string | null) => void;
}

function QualificationMatrix({
  calls,
  data,
  qualification,
  opportunityId,
  framework,
  onFrameworkChange,
  openField,
  onOpenFieldChange,
}: QualificationMatrixProps) {
  const keys = framework === 'bant' ? bantKeys : meddpiccKeys;

  return (
    <div className="space-y-3">
      <div className="flex gap-1 rounded-lg bg-app-canvas p-1 w-fit" aria-label="Qualification framework">
        {(['bant', 'meddpicc'] as const).map((f) => (
          <button
            key={f}
            aria-pressed={framework === f}
            className={'rounded-md text-xs px-3 py-1.5 ' + (framework === f ? 'bg-app-surface shadow-sm text-app-ink' : 'text-app-muted')}
            onClick={() => onFrameworkChange(f)}
          >
            {f.toUpperCase()}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {keys.map((key) => {
          const items = evidenceByField(calls, key);
          const manual = qualification?.[framework]?.[key];
          const state = fieldState(items, manual);
          const isOpen = openField === key;
          return (
            <div key={key} className="border border-app-line rounded-xl overflow-hidden">
              <button
                className="w-full p-3 text-left flex items-center justify-between gap-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-app-focus"
                aria-expanded={isOpen}
                onClick={() => onOpenFieldChange(isOpen ? null : key)}
              >
                <div>
                  <span className="block text-sm font-semibold text-app-ink">{playbookFields[key as keyof typeof playbookFields]}</span>
                  <span className={'block text-xs mt-1 ' + fieldStateClass(state)}>{state}</span>
                </div>
                {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
              {isOpen && (
                <div className="p-3 pt-0 space-y-3 text-xs">
                  {manual?.value && manual.source !== 'live_mavis' && <p className="text-app-ink">Rep reviewed: {manual.value}</p>}
                  {items.map((e: any) => (
                    <div key={e.id}>
                      <p className="text-app-ink leading-5">“{e.quote}”</p>
                      <div className="text-app-muted mt-1">
                        {e.speaker} · {e.side} · <Link className="underline underline-offset-2" to={meetingSourceUrl(opportunityId, e.meeting_session_id, e.time, e.bot_id)}>{e.meeting_title} · {time(e.time)}</Link>
                      </div>
                    </div>
                  ))}
                  {!items.some((e) => e.side === 'customer') && (
                    <p className="text-app-muted">Ask: {data?.questions?.find((q: any) => q.field === key)?.question ?? 'What can the customer confirm about this?'}</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-app-muted">Customer reported is attributed evidence, not independent verification. Seller and partner observations do not qualify the customer.</p>
    </div>
  );
}

interface SpeakerSidesProps {
  calls: any[];
  busy: boolean;
  onSideChange: (call: any, speakerKey: string, side: string) => void;
}

function SpeakerSides({ calls, busy, onSideChange }: SpeakerSidesProps) {
  return (
    <div className="space-y-3">
      {calls.map((call: any) => (
        <div key={call.id} className="space-y-2">
          {Object.entries(call.participants ?? {}).map(([key, p]: any) => (
            <div key={key} className="flex flex-wrap items-center justify-between gap-2 border border-app-line rounded-lg p-2.5">
              <div className="min-w-0">
                <span className="block text-xs font-semibold text-app-ink">{p.name}</span>
                <span className="text-[11px] text-app-muted break-all">{p.email ?? 'Email unavailable'} · {call.meeting_title}</span>
              </div>
              <select
                aria-label={'Speaker side for ' + p.name}
                value={p.side}
                disabled={busy}
                className="rounded-lg bg-app-canvas border border-app-line text-xs p-1.5"
                onChange={(e) => onSideChange(call, key, e.target.value)}
              >
                <option value="unknown">Unknown side</option>
                <option value="customer">Customer</option>
                <option value="internal">Seller / internal</option>
                <option value="partner">Partner</option>
              </select>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function HookCard({ evidence, opportunityId }: { evidence: any; opportunityId: string }) {
  const e = evidence;
  return (
    <div className="rounded-xl border border-app-line p-3 motion-safe:animate-[fadeIn_200ms_ease-out]">
      <p className="text-xs font-semibold text-app-ink mb-1">{fieldLabel(e.field)}</p>
      <p className="text-sm text-app-ink leading-6">“{e.quote}”</p>
      <p className="text-[11px] text-app-muted mt-2">
        {e.speaker} · {sideLabel(e.side)} · <Link to={meetingSourceUrl(opportunityId, e.meeting_session_id, e.time, e.bot_id)} className="underline">{time(e.time)}</Link>
      </p>
    </div>
  );
}

function QuestionCard({ q, live, opportunityId }: { q: any; live: boolean; opportunityId: string }) {
  const [pulse, setPulse] = useState(live && q.state !== 'suggested');

  // Briefly highlight a question when MAVIS hears it being asked or answered.
  useEffect(() => {
    if (q.state === 'suggested') {
      setPulse(false);
      return;
    }
    setPulse(true);
    const t = setTimeout(() => setPulse(false), 2400);
    return () => clearTimeout(t);
  }, [q.state]);

  const borderClass = q.state === 'suggested' ? 'border-app-line' : 'border-app-accent';
  const pulseClass = pulse && live ? ' motion-safe:animate-pulse ring-2 ring-app-accent/40' : '';

  return (
    <div className={'rounded-xl bg-app-canvas p-3 border transition-colors ' + borderClass + pulseClass}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-xs font-medium text-app-muted">{playbookFields[q.field as keyof typeof playbookFields] ?? 'Progress the deal'}</p>
        <span role="status" className="text-[11px] font-medium text-app-ink">
          {QUESTION_STATE_LABELS[q.state as keyof typeof QUESTION_STATE_LABELS] ?? 'Suggested'}
        </span>
      </div>
      <p className="text-sm text-app-ink leading-6">{q.question}</p>
      {q.asked && <p className="text-[11px] text-app-muted mt-2">Asked by {q.asked.speaker}: “{q.asked.quote}”</p>}
      {q.answers?.map((a: any) => (
        <p key={a.source_event_id + ':' + a.quote} className="text-xs text-app-ink mt-2">
          {a.speaker}: “{a.quote}” <Link className="underline text-app-muted" to={meetingSourceUrl(opportunityId, q.meeting_session_id, a.time, a.bot_id)}>View source</Link>
        </p>
      ))}
      {q.quote && !q.asked && <p className="text-[11px] text-app-muted mt-2">Based on {q.speaker ?? 'captured speech'}: “{q.quote}”</p>}
    </div>
  );
}

export default function MavisLivePlaybook({ meetingId, opportunityId, live = false, view = 'meeting', qualification, onChange }: Props) {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<InsightTab>('hooks');
  const [framework, setFramework] = useState<Framework>('bant');
  const [open, setOpen] = useState<string | null>(null);
  const latest = useRef<any>(null);
  const active = useRef(true);
  const polling = useRef(false);
  const fingerprint = useRef('');
  const changed = useRef(onChange);
  latest.current = data;
  changed.current = onChange;

  const refresh = useCallback(async () => {
    if (polling.current) return;
    polling.current = true;
    try {
      const result = await getLivePlaybook(opportunityId, meetingId);
      if (!active.current) return;
      setData(result);
      setError('');
      // Notify the parent when any call's insights changed since the previous snapshot.
      const nextFingerprint = JSON.stringify(result.calls?.map((c: any) => [c.id, c.revision]));
      if (nextFingerprint !== fingerprint.current) {
        if (fingerprint.current) changed.current?.();
        fingerprint.current = nextFingerprint;
      }
    } catch (e) {
      if (active.current) setError(meetingErrorMessage(e, 'Meeting insights unavailable. Transcript capture continues.'));
    } finally {
      polling.current = false;
    }
  }, [meetingId, opportunityId]);

  useEffect(() => {
    active.current = true;
    void refresh();
    const interval = setInterval(() => {
      const shouldPoll = live || view !== 'meeting' || isAnalysing(latest.current);
      if (document.visibilityState === 'visible' && shouldPoll) void refresh();
    }, live ? LIVE_POLL_INTERVAL_MS : IDLE_POLL_INTERVAL_MS);
    return () => {
      active.current = false;
      clearInterval(interval);
    };
  }, [refresh, live, view]);

  // Runs a write for one meeting, then reloads the playbook and notifies the parent.
  const runAction = async (id: string | undefined, write: (id: string) => Promise<unknown>) => {
    if (!id || busy) return;
    setBusy(true);
    setError('');
    try {
      await write(id);
      await refresh();
      changed.current?.();
    } catch (e) {
      setError(meetingErrorMessage(e, 'Could not save this change.'));
    } finally {
      setBusy(false);
    }
  };

  const saveTask: TaskSaveHandler = (task, call, status, notes) => {
    void runAction(call.meeting_session_id, (id) => updatePlaybookTask(id, task.id, { status, notes, revision: call.revision }));
  };
  const saveSpeakerSide = (call: any, speakerKey: string, side: string) => {
    void runAction(call.meeting_session_id, (id) => updatePlaybookSpeakerSide(id, { speaker_key: speakerKey, side }));
  };

  const calls: any[] = data?.calls ?? [];
  const evidence = calls.flatMap((c: any) => c.evidence.map((e: any) => ({
    ...e,
    meeting_session_id: c.meeting_session_id,
    meeting_title: c.meeting_title,
  })));
  const current = calls[0];
  const unknownSpeakers = hasUnknownSpeakers(current);

  const taskView = <TaskList calls={calls} busy={busy} onSave={saveTask} />;
  const people = <SpeakerSides calls={calls} busy={busy} onSideChange={saveSpeakerSide} />;

  if (view === 'sync') return null;

  if (view === 'tasks') {
    return (
      <section className="card space-y-4" aria-label="Opportunity tasks">
        <h3 className="font-semibold text-app-ink">Tasks</h3>
        {error ? <p role="alert" className="text-xs text-app-critical">{error}</p> : !data ? <p className="text-sm text-app-muted">Loading tasks…</p> : taskView}
      </section>
    );
  }

  if (view === 'stakeholders') {
    return (
      <section className="card space-y-3">
        <h3 className="font-semibold text-app-ink flex items-center gap-2">
          <Users className="w-4 h-4" />
          Meeting attendees
        </h3>
        <p className="text-xs text-app-muted">Resolve speaker sides here. Only customer statements populate qualification.</p>
        {error && <p role="alert" className="text-xs text-app-critical">{error}</p>}
        {people}
      </section>
    );
  }

  const tabs = [
    ['hooks', 'Hooks', Quote],
    ['playbook', 'Playbook', Target],
    ['questions', live ? 'Ask now' : 'Next meeting', MessageCircle],
  ] as const;

  return (
    <section className="card !p-4 space-y-4" aria-label={live ? 'Live meeting insights' : 'Meeting playbook'}>
      <header className="flex items-center justify-between gap-2">
        <h3 className="font-semibold text-app-ink flex gap-2 items-center">
          <Sparkles className="w-4 h-4" />
          {live ? 'Live insights' : 'Playbook'}
        </h3>
        {current?.processing_status === 'processing' && (
          <span role="status" className="text-xs text-app-muted flex gap-1.5 items-center">
            <span className="w-1.5 h-1.5 rounded-full bg-app-accent motion-safe:animate-pulse" />
            Listening for insights
          </span>
        )}
      </header>

      {error && (
        <div role="alert" className="text-xs text-app-critical">
          {error}
          <button className="btn-ghost !text-xs" onClick={() => void refresh()}>Retry loading</button>
        </div>
      )}

      {!data ? (
        <div role="status" className="h-24 rounded-xl bg-app-canvas motion-safe:animate-pulse" />
      ) : data.enabled === false ? (
        <p className="text-sm text-app-muted">Live meeting insights are turned off for your organisation, so meetings are not analysed by AI. Transcript capture remains available. An organisation administrator can turn on Live Meeting Insights in the MAVIS settings.</p>
      ) : (
        <>
          {current?.last_error && (
            <div role="alert" className="rounded-lg border border-app-line p-3 text-xs text-app-muted">
              {current.last_error}
              <button
                disabled={busy}
                onClick={() => void runAction(current.meeting_session_id, retryLivePlaybook)}
                className="btn-ghost !text-xs mt-1"
              >
                <RotateCcw className="w-3 h-3" />
                Retry analysis
              </button>
            </div>
          )}

          {unknownSpeakers && (
            <button className="btn-ghost !text-xs" onClick={() => setTab('playbook')}>
              <Users className="w-3.5 h-3.5" />
              Confirm speaker sides for accurate qualification
            </button>
          )}

          <div className="flex gap-1 border-b border-app-line pb-2" aria-label="Meeting insights sections">
            {tabs.map(([id, label, Icon]) => (
              <button
                key={id}
                aria-pressed={tab === id}
                onClick={() => setTab(id)}
                className={'flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs ' + (tab === id ? 'bg-app-accent text-app-ink' : 'text-app-muted hover:bg-app-canvas')}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>

          {tab === 'hooks' && (
            <div className="max-h-[45vh] overflow-y-auto space-y-2" aria-live="polite">
              {!evidence.length ? (
                <p className="text-sm text-app-muted leading-6">
                  {live ? 'Important statements will appear here as finalized speech is analysed.' : 'No qualification hooks were captured.'}
                </p>
              ) : (
                evidence.slice().reverse().map((e: any) => <HookCard key={e.id} evidence={e} opportunityId={opportunityId} />)
              )}
            </div>
          )}

          {tab === 'playbook' && (
            <>
              <QualificationMatrix
                calls={calls}
                data={data}
                qualification={qualification}
                opportunityId={opportunityId}
                framework={framework}
                onFrameworkChange={setFramework}
                openField={open}
                onOpenFieldChange={setOpen}
              />
              <details open={unknownSpeakers} className="text-xs text-app-muted">
                <summary className="cursor-pointer">Speaker attribution</summary>
                <div className="mt-3">{people}</div>
              </details>
            </>
          )}

          {tab === 'questions' && (
            <div className="space-y-3">
              {visibleQuestions(data).map((q: any) => (
                <QuestionCard key={q.id ?? q.question} q={q} live={live} opportunityId={opportunityId} />
              ))}
              {data.prior_tasks?.length > 0 && (
                <div className="border-t border-app-line pt-3">
                  <h4 className="text-xs font-semibold text-app-ink mb-2">Remember to follow up</h4>
                  {data.prior_tasks.map((t: any) => (
                    <p key={t.id} className="text-xs text-app-muted leading-6">{t.owner ?? 'Owner not stated'}: {t.action}{t.due ? ` · ${t.due}` : ''}</p>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-app-muted">Suggested questions are not agreed tasks.</p>
            </div>
          )}

          {!live && (
            <details>
              <summary className="text-sm font-semibold text-app-ink cursor-pointer">Agreed actions</summary>
              <div className="mt-3">{taskView}</div>
            </details>
          )}

          {!calls.length && meetingId && (
            <button disabled={busy} className="btn-ghost !text-xs" onClick={() => void runAction(meetingId, processLivePlaybook)}>
              {live ? 'Recover available insights' : 'Analyse captured transcript'}
            </button>
          )}
        </>
      )}
    </section>
  );
}
